import React, { useCallback, useRef } from 'react';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import { Input } from '@tviklax/ui-components';

export interface AddressAutofillFields {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface AddressAutocompleteProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddressSelected: (fields: AddressAutofillFields) => void;
  placeholder?: string;
  required?: boolean;
}

// Module-level constant so @react-google-maps/api always sees the same
// array reference across renders (a new array each render triggers a
// "Performance warning! LoadScript has been reloaded" from the library).
const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places'];

// Scoped to the platform's actual trade lane for now — US and India.
const SUPPORTED_COUNTRY_CODES = ['us', 'in'];

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  label,
  name,
  value,
  onChange,
  onAddressSelected,
  placeholder,
  required,
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const onLoad = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    try {
      autocomplete.setComponentRestrictions({ country: SUPPORTED_COUNTRY_CODES });
      autocomplete.setFields(['address_components', 'formatted_address']);
      autocompleteRef.current = autocomplete;
    } catch (err) {
      console.error('Google Places Autocomplete failed to initialize:', err);
    }
  }, []);

  const onPlaceChanged = useCallback(() => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place || !place.address_components) return;

    let streetNumber = '';
    let route = '';
    let sublocalityLevel1 = '';
    let sublocalityLevel2 = '';
    let neighborhood = '';
    let locality = '';
    let stateCode = '';
    let postalCode = '';
    let countryCode = '';

    place.address_components.forEach((component) => {
      component.types.forEach((type) => {
        switch (type) {
          case 'street_number':
            streetNumber = component.long_name;
            break;
          case 'route':
            route = component.long_name;
            break;
          case 'sublocality_level_1':
            sublocalityLevel1 = component.long_name;
            break;
          case 'sublocality_level_2':
            sublocalityLevel2 = component.long_name;
            break;
          case 'neighborhood':
            neighborhood = component.long_name;
            break;
          case 'locality':
            locality = component.long_name;
            break;
          case 'administrative_area_level_1':
            stateCode = component.short_name;
            break;
          case 'postal_code':
            postalCode = component.long_name;
            break;
          case 'country':
            countryCode = component.short_name;
            break;
        }
      });
    });

    let street = `${streetNumber} ${route}`.trim();
    if (neighborhood) street += ` ${neighborhood}`;
    if (sublocalityLevel2) street += ` ${sublocalityLevel2}`;
    if (sublocalityLevel1) street += ` ${sublocalityLevel1}`;
    street = street.trim();

    onAddressSelected({
      street,
      city: locality,
      state: stateCode,
      postalCode,
      country: countryCode,
    });
  }, [onAddressSelected]);

  // Google's script failed to load or hasn't loaded yet — fall back to a
  // plain input rather than blocking the form.
  if (loadError || !isLoaded) {
    return (
      <Input
        label={label}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
      />
    );
  }

  return (
    <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
      <Input
        label={label}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
      />
    </Autocomplete>
  );
};
